

export interface APIHealth {
  name: string;
  url: string;
  status: 'healthy' | 'warning' | 'error' | 'loading';
  responseTime?: number;
  lastChecked?: string;
  error?: string;
  uptime?: number;
  requestsPerMinute?: number;
  details?: Record<string, any>;
}

export interface Environment {
  name: string;
  label: string;
  apis: APIHealth[];
}

class APIHealthService {
  private static instance: APIHealthService;
  private healthData: Map<string, APIHealth[]> = new Map();
  private listeners: Set<(environment: string, data: APIHealth[]) => void> = new Set();

  private constructor() {}

  static getInstance(): APIHealthService {
    if (!APIHealthService.instance) {
      APIHealthService.instance = new APIHealthService();
    }
    return APIHealthService.instance;
  }

  // Frontend health check - checks if the page loads successfully
  private async checkFrontendHealth(url: string, name: string): Promise<APIHealth> {
    const startTime = Date.now();
    
    try {
      console.log(`Checking frontend health for: ${url}`);
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'User-Agent': 'Aletheia-Admin-Health-Check/1.0'
        }
      });

      const responseTime = Date.now() - startTime;
      const status = response.status;

      // Frontend health check logic
      if (status === 200) {
        return {
          name,
          url,
          status: 'healthy',
          responseTime,
          lastChecked: new Date().toISOString(),
          details: { statusCode: status }
        };
      } else if (status === 404) {
        return {
          name,
          url,
          status: 'error',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: 'Page not found (404)',
          details: { statusCode: status }
        };
      } else if (status === 503) {
        return {
          name,
          url,
          status: 'error',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: 'Service unavailable (503)',
          details: { statusCode: status }
        };
      } else if (status >= 500) {
        return {
          name,
          url,
          status: 'error',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: `Server error (${status})`,
          details: { statusCode: status }
        };
      } else if (status >= 400) {
        return {
          name,
          url,
          status: 'warning',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: `Client error (${status})`,
          details: { statusCode: status }
        };
      } else {
        return {
          name,
          url,
          status: 'warning',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: `Unexpected status: ${status}`,
          details: { statusCode: status }
        };
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      let errorMessage = 'Network error';
      let errorDetails: Record<string, any> = { error: 'Unknown error' };
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'CORS/Network blocked - service may be accessible but not from this origin';
          errorDetails = { 
            error: error.message,
            type: 'cors_network_blocked',
            suggestion: 'Service may require CORS configuration or Application Gateway routing'
          };
        } else {
          errorMessage = error.message;
          errorDetails = { error: error.message };
        }
      } else {
        errorDetails = { error: String(error) };
      }
      
      return {
        name,
        url,
        status: 'error',
        responseTime,
        lastChecked: new Date().toISOString(),
        error: errorMessage,
        details: errorDetails
      };
    }
  }

  // API health check - checks if the API endpoint responds
  private async checkAPIHealth(url: string, name: string): Promise<APIHealth> {
    const startTime = Date.now();
    
    try {
      console.log(`Checking API health for: ${url}`);
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Aletheia-Admin-Health-Check/1.0'
        }
      });

      const responseTime = Date.now() - startTime;
      const status = response.status;

      if (status === 200) {
        let details: Record<string, any> = { statusCode: status };
        try {
          const data = await response.json();
          details = { ...details, data };
        } catch {
          // If not JSON, that's fine for health checks
        }

        return {
          name,
          url,
          status: 'healthy',
          responseTime,
          lastChecked: new Date().toISOString(),
          details
        };
      } else if (status === 404) {
        return {
          name,
          url,
          status: 'error',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: 'API endpoint not found (404)',
          details: { statusCode: status }
        };
      } else if (status === 503) {
        return {
          name,
          url,
          status: 'error',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: 'Service unavailable (503)',
          details: { statusCode: status }
        };
      } else if (status >= 500) {
        return {
          name,
          url,
          status: 'error',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: `Server error (${status})`,
          details: { statusCode: status }
        };
      } else if (status >= 400) {
        return {
          name,
          url,
          status: 'warning',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: `Client error (${status})`,
          details: { statusCode: status }
        };
      } else {
        return {
          name,
          url,
          status: 'warning',
          responseTime,
          lastChecked: new Date().toISOString(),
          error: `Unexpected status: ${status}`,
          details: { statusCode: status }
        };
      }
    } catch (error) {
      const responseTime = Date.now() - startTime;
      let errorMessage = 'Network error';
      let errorDetails: Record<string, any> = { error: 'Unknown error' };
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'CORS/Network blocked - service may be accessible but not from this origin';
          errorDetails = { 
            error: error.message,
            type: 'cors_network_blocked',
            suggestion: 'Service may require CORS configuration or Application Gateway routing'
          };
        } else {
          errorMessage = error.message;
          errorDetails = { error: error.message };
        }
      } else {
        errorDetails = { error: String(error) };
      }
      
      return {
        name,
        url,
        status: 'error',
        responseTime,
        lastChecked: new Date().toISOString(),
        error: errorMessage,
        details: errorDetails
      };
    }
  }

  async checkAllHealth(): Promise<Map<string, APIHealth[]>> {
    const environments: Environment[] = [
      {
        name: 'production',
        label: 'Production',
        apis: [
          { name: 'PropTrak Backend API', url: 'https://api.proptrak.ai/api/health', status: 'loading' },
          { name: 'Aletheia Backend API', url: 'https://api.aletheiatech.ai/api/health', status: 'loading' },
          { name: 'PropTrak Frontend', url: 'https://proptrak.ai', status: 'loading' },
          { name: 'Aletheia Frontend', url: 'https://aletheiatech.ai', status: 'loading' }
        ]
      },
      {
        name: 'staging',
        label: 'Staging',
        apis: [
          { name: 'PropTrak Backend API', url: 'https://proptrak-backend-staging-h4c4fpdkefxhubt.westus2-01.azurewebsites.net/api/health', status: 'loading' },
          { name: 'Aletheia Backend API', url: 'https://aletheia-admin-backend-staging-gchhdhd0ewh8cqa5.westus2-01.azurewebsites.net/api/health', status: 'loading' }
        ]
      }
    ];

    for (const env of environments) {
      const healthChecks: APIHealth[] = [];

      for (const api of env.apis) {
        // Set initial loading state
        healthChecks.push({
          ...api,
          status: 'loading',
          lastChecked: new Date().toISOString()
        });

        // Update the map immediately with loading state
        this.healthData.set(env.name, [...healthChecks]);
        this.notifyListeners(env.name, [...healthChecks]);

        // Perform the health check
        let healthResult: APIHealth;
        if (api.name.includes('Frontend')) {
          healthResult = await this.checkFrontendHealth(api.url, api.name);
        } else {
          healthResult = await this.checkAPIHealth(api.url, api.name);
        }

        // Update the result
        const index = healthChecks.findIndex(h => h.name === api.name);
        if (index !== -1) {
          healthChecks[index] = healthResult;
        }

        // Update the map and notify listeners
        this.healthData.set(env.name, [...healthChecks]);
        this.notifyListeners(env.name, [...healthChecks]);
      }
    }

    return this.healthData;
  }

  getHealthData(environment: string): APIHealth[] {
    return this.healthData.get(environment) || [];
  }

  getAllHealthData(): Map<string, APIHealth[]> {
    return new Map(this.healthData);
  }

  addListener(listener: (environment: string, data: APIHealth[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(environment: string, data: APIHealth[]): void {
    this.listeners.forEach(listener => {
      try {
        listener(environment, data);
      } catch (error) {
        console.error('Error in health check listener:', error);
      }
    });
  }

  // Manual health check for a specific endpoint
  async checkEndpointHealth(url: string, name: string, isFrontend: boolean = false): Promise<APIHealth> {
    if (isFrontend) {
      return this.checkFrontendHealth(url, name);
    } else {
      return this.checkAPIHealth(url, name);
    }
  }
}

export const apiHealthService = APIHealthService.getInstance();
export default apiHealthService; 